import React, { useEffect, useMemo, useRef, useState } from "react";
import EmailComposer from "../EmailComposer";
import EmailThread from "../EmailThread";
import LeadStatusBadge from "../LeadStatusBadge";
import { STAGE_OPTIONS, STAGE_LABELS, ENROLLMENT_OPTIONS, ENROLLMENT_LABELS } from "../constants";
import {
  buildThreadMessages,
  classNames,
  formatDateTime,
  formatRelative,
  primaryContactEmail,
} from "./utils";
import SnoozePopover from "./SnoozePopover";

function Section({ title, children, action, padding = "p-4" }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card">
      <header className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          {title}
        </h3>
        {action}
      </header>
      <div className={padding}>{children}</div>
    </section>
  );
}

export default function LeadDetailDrawer({
  detail,
  loading,
  activeCampaignId,
  activeCampaign,
  onClose,
  onPatchCampaignLead,
  onSaveNotes,
  onSnooze,
  onSkipStep,
  onEmailSent,
  onNext,
  onPrevious,
  gmailConnected,
  onConnectGmail,
}) {
  const [composeRecipient, setComposeRecipient] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const snoozeAnchorRef = useRef(null);

  useEffect(() => {
    setNoteDraft(detail?.lead?.notes || "");
    setComposeRecipient("");
  }, [detail?.lead?._id, detail?.lead?.notes]);

  const selectedCampaignLead = useMemo(() => {
    if (!detail?.campaigns?.length) return null;
    return (
      detail.campaigns.find((entry) => entry.campaign?._id === activeCampaignId)?.campaignLead ||
      null
    );
  }, [activeCampaignId, detail]);

  const threadMessages = useMemo(() => buildThreadMessages(detail), [detail]);

  if (!detail?.lead) return null;

  const lead = detail.lead;
  const cl = selectedCampaignLead;

  const handleSaveNotes = async () => {
    if (!lead._id) return;
    setSavingNotes(true);
    try {
      await onSaveNotes?.(lead._id, noteDraft);
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-30 bg-zinc-900/30 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      <aside className="fixed right-0 top-0 z-40 flex h-full w-full max-w-[560px] flex-col border-l border-zinc-200 bg-white shadow-panel animate-slide-in">
        <div className="border-b border-zinc-200 bg-white px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                Lead detail
              </p>
              <h2 className="mt-0.5 truncate text-xl font-semibold tracking-tight text-zinc-900">
                {lead.company_name || "Untitled lead"}
              </h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                <LeadStatusBadge status={cl?.stage || lead.status || "new"} />
                <span>·</span>
                <span className="truncate">
                  {primaryContactEmail(lead) || "No primary email"}
                </span>
                {lead.website ? (
                  <>
                    <span>·</span>
                    <a
                      href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-indigo-600 hover:underline"
                    >
                      {lead.website.replace(/^https?:\/\//, "")}
                    </a>
                  </>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={onPrevious}
                disabled={!onPrevious}
                title="Previous (k)"
                className="rounded-md px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:text-zinc-300"
              >
                ↑
              </button>
              <button
                onClick={onNext}
                disabled={!onNext}
                title="Next (j)"
                className="rounded-md px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:text-zinc-300"
              >
                ↓
              </button>
              <button
                onClick={onClose}
                title="Close (Esc)"
                className="rounded-md px-2 py-1.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100"
              >
                Close
              </button>
            </div>
          </div>

          {cl ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 text-xs text-zinc-600">
                <span className="text-zinc-400">Stage</span>
                <select
                  value={cl.stage || ""}
                  onChange={(event) =>
                    onPatchCampaignLead?.(cl._id, { stage: event.target.value })
                  }
                  className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-800 focus:border-indigo-500 focus:outline-none"
                >
                  {STAGE_OPTIONS.map((stage) => (
                    <option key={stage} value={stage}>
                      {STAGE_LABELS[stage]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1 text-xs text-zinc-600">
                <span className="text-zinc-400">Enrollment</span>
                <select
                  value={cl.enrollmentStatus || ""}
                  onChange={(event) =>
                    onPatchCampaignLead?.(cl._id, {
                      enrollmentStatus: event.target.value,
                    })
                  }
                  className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-800 focus:border-indigo-500 focus:outline-none"
                >
                  {ENROLLMENT_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {ENROLLMENT_LABELS[status]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <button
                  ref={snoozeAnchorRef}
                  onClick={() => setSnoozeOpen((v) => !v)}
                  className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
                >
                  Snooze
                </button>
                {snoozeOpen ? (
                  <SnoozePopover
                    anchorRef={snoozeAnchorRef}
                    onSelect={(date) => onSnooze?.(cl._id, date)}
                    onClose={() => setSnoozeOpen(false)}
                  />
                ) : null}
              </div>
              <button
                onClick={() => onSkipStep?.(cl)}
                className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
              >
                Skip step
              </button>
              <button
                onClick={() =>
                  onPatchCampaignLead?.(cl._id, { enrollmentStatus: "unsubscribed" })
                }
                className="rounded-md border border-zinc-300 bg-white px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-red-50 hover:text-red-700"
              >
                Unsubscribe
              </button>
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto bg-zinc-50/40">
          <div className="space-y-3 p-4">
            {cl ? (
              <Section title="Next action">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <div>
                    <p className="text-xs text-zinc-500">Scheduled</p>
                    <p className="mt-0.5 font-medium text-zinc-900">
                      {cl.nextActionAt ? formatDateTime(cl.nextActionAt) : "Not scheduled"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Current step</p>
                    <p className="mt-0.5 font-medium text-zinc-900">
                      Step {(cl.stepIndex ?? 0) + 1}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Last touch</p>
                    <p className="mt-0.5 font-medium text-zinc-900">
                      {formatRelative(cl.lastSentAt || lead.updatedAt)}
                    </p>
                  </div>
                </div>
              </Section>
            ) : null}

            <Section title="Compose">
              {gmailConnected ? (
                <EmailComposer
                  leadId={lead._id}
                  campaignId={activeCampaignId}
                  campaignLeadId={cl?._id || null}
                  initialTo={composeRecipient}
                  threadId={cl?.latestThreadId || null}
                  initialFrom={activeCampaign?.senderAlias || ""}
                  onClearTo={() => setComposeRecipient("")}
                  onSent={async (payload) => {
                    await onEmailSent?.(payload);
                    setComposeRecipient("");
                  }}
                />
              ) : (
                <button
                  onClick={onConnectGmail}
                  className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                >
                  Connect Gmail to send
                </button>
              )}
            </Section>

            <Section title="Thread">
              {threadMessages.length ? (
                <EmailThread
                  messages={threadMessages}
                  leadId={lead._id}
                  campaignId={activeCampaignId}
                  campaignLeadId={cl?._id || null}
                  onReply={({ from }) => setComposeRecipient(from)}
                />
              ) : (
                <p className="text-sm text-zinc-500">No messages exchanged yet.</p>
              )}
            </Section>

            <Section title="Contacts">
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-zinc-500">Emails</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(lead.emails || []).map((entry) => (
                      <button
                        key={entry.address}
                        onClick={() => setComposeRecipient(entry.address)}
                        className={classNames(
                          "rounded-md px-2 py-1 text-xs font-medium transition-colors",
                          composeRecipient === entry.address
                            ? "bg-indigo-600 text-white"
                            : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                        )}
                      >
                        {entry.address}
                      </button>
                    ))}
                    {!(lead.emails || []).length ? (
                      <span className="text-xs text-zinc-500">No email on file</span>
                    ) : null}
                  </div>
                </div>

                {(lead.people || []).length ? (
                  <div>
                    <p className="text-xs font-medium text-zinc-500">People</p>
                    <div className="mt-1.5 space-y-1.5">
                      {lead.people.map((person) => {
                        const name =
                          person.full_name ||
                          [person.first_name, person.last_name].filter(Boolean).join(" ") ||
                          "Unnamed";
                        return (
                          <div
                            key={`${name}-${person.email || ""}`}
                            className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-zinc-900">{name}</p>
                              <p className="truncate text-xs text-zinc-500">
                                {person.title || "No title"}
                              </p>
                            </div>
                            {person.email ? (
                              <button
                                onClick={() => setComposeRecipient(person.email)}
                                className="rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                              >
                                Compose
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </Section>

            <Section
              title="Internal notes"
              action={
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  className="rounded-md bg-zinc-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300"
                >
                  {savingNotes ? "Saving…" : "Save"}
                </button>
              }
            >
              <textarea
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                rows={4}
                placeholder="What to remember about this lead…"
                className="w-full resize-none rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-800 placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </Section>

            <Section title="Recent activity">
              {(detail.activities || []).length ? (
                <ul className="divide-y divide-zinc-100">
                  {detail.activities.slice(0, 10).map((activity) => (
                    <li
                      key={activity._id}
                      className="flex items-start justify-between gap-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-zinc-900">
                          <span className="mr-1.5 inline-flex items-center rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
                            {activity.direction}
                          </span>
                          {activity.subject || "No subject"}
                        </p>
                        <p className="truncate text-xs text-zinc-500">
                          {(activity.text || "").slice(0, 120)}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs text-zinc-400">
                        {formatRelative(activity.occurredAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-zinc-500">No activity recorded yet.</p>
              )}
            </Section>
          </div>
        </div>
      </aside>
    </>
  );
}
